import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { Bookmark, Download, MoreVertical, Pencil, Plus, Trash2, TrendingUp } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Select } from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { CompositionChart, TimeseriesChart } from "@/components/ui/chart"
import { cn } from "@/lib/utils"
import type { PageProps } from "@/types/inertia"

type Filters = {
  dimension_type: string
  dimension_value: string | null
  gender: string | null
  brand: string | null
  subcat: string | null
  subsegb: string | null
}

type FilterOptions = {
  dimension_types: string[]
  dimension_values: string[]
  genders: string[]
  brands: string[]
  subcats: string[]
  subsegbs: string[]
}

type Timeseries = {
  months: { year: number; month: number; label: string }[]
  series: { key: string; name: string }[]
  rows: Array<Record<string, number | string>>
}

type Period = { year: number; month: number; label: string }

type CompositionSlice = { label: string; value: number; share: number }
type Composition = { total: number; slices: CompositionSlice[] }

type TopSku = {
  brand: string
  variant: string | null
  packsize: string | null
  value: number
  prev_value: number | null
  growth_pct: number | null
}

type SavedView = {
  id: number
  name: string
  filters: Record<string, string | null>
}

type DashboardProps = PageProps<{
  filters: Filters
  breakdown: string
  breakdowns: string[]
  filter_options: FilterOptions
  timeseries: Timeseries
  periods: Period[]
  period: string | null
  share_of_wallet: { channel: Composition; region: Composition }
  top_skus: TopSku[]
  saved_views: SavedView[]
}>

const DIMENSION_TYPE_LABELS: Record<string, string> = {
  reconciled_channel: "Channel (rekonsiliasi)",
  channel: "Channel (mentah)",
  area: "Area / Region",
  summary: "Summary (nasional)",
}

const BREAKDOWN_LABELS: Record<string, string> = {
  dimension_value: "Per nilai dimensi",
  brand: "Per brand",
  none: "Total (satu garis)",
}

// Compact Indonesian notation: Rb (ribu) / Jt (juta) / M (miliar) / T (triliun).
function formatCompact(n: number): string {
  if (n == null || Number.isNaN(n)) return "—"
  const abs = Math.abs(n)
  if (abs < 1000) return new Intl.NumberFormat("id-ID", { maximumFractionDigits: 0 }).format(n)
  const sign = n < 0 ? "-" : ""
  const units: [number, string][] = [
    [1e12, "T"],
    [1e9, "M"],
    [1e6, "Jt"],
    [1e3, "Rb"],
  ]
  for (const [factor, suffix] of units) {
    if (abs >= factor) {
      const scaled = abs / factor
      const decimals = scaled >= 100 ? 0 : 1
      const num = new Intl.NumberFormat("id-ID", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(scaled)
      return `${sign}${num} ${suffix}`
    }
  }
  return new Intl.NumberFormat("id-ID").format(n)
}

export default function Dashboard({
  filters,
  breakdown,
  breakdowns,
  filter_options,
  timeseries,
  periods,
  period,
  share_of_wallet,
  top_skus,
  saved_views,
}: DashboardProps) {
  // The full active query (filters + breakdown + period), used both to drive
  // Inertia navigations and to build the Excel export download URL.
  const activeQuery = React.useMemo(() => {
    const next: Record<string, string> = {}
    const merged = { ...filters, breakdown, period } as Record<string, unknown>
    for (const [key, value] of Object.entries(merged)) {
      if (value != null && value !== "") next[key] = String(value)
    }
    return next
  }, [filters, breakdown, period])

  // Push the next filter state into the URL; Inertia re-runs the action and
  // re-renders with fresh props. preserveState keeps the form responsive; the
  // query string is the single shareable source of truth.
  function update(patch: Partial<Filters & { breakdown: string; period: string | null }>) {
    const next: Record<string, string> = { ...activeQuery }
    for (const [key, value] of Object.entries(patch)) {
      if (value == null || value === "") delete next[key]
      else next[key] = String(value)
    }
    router.get("/admin/niq-fragrances/dashboard", next, {
      preserveState: true,
      preserveScroll: true,
      replace: true,
    })
  }

  const exportUrl =
    "/admin/niq-fragrances/dashboard/export?" + new URLSearchParams(activeQuery).toString()
  const hasData = timeseries.months.length > 0
  const selectedPeriodLabel = periods.find((p) => `${p.year}-${p.month}` === period)?.label ?? null

  // Saved views state
  const [saveOpen, setSaveOpen] = React.useState(false)
  const [saveName, setSaveName] = React.useState("")
  const [saving, setSaving] = React.useState(false)
  const [renameView, setRenameView] = React.useState<SavedView | null>(null)
  const [renameName, setRenameName] = React.useState("")
  const [renaming, setRenaming] = React.useState(false)

  // A saved view matches the active query when all its non-null filter keys equal
  // the corresponding values in activeQuery.
  function isActiveView(view: SavedView): boolean {
    const vf = view.filters
    const keys = Object.keys(vf)
    if (keys.length === 0) return false
    return keys.every((k) => {
      const vVal = vf[k]
      const aVal = activeQuery[k] ?? null
      if (vVal == null || vVal === "") return aVal == null || aVal === ""
      return String(vVal) === String(aVal)
    })
  }

  function applyView(view: SavedView) {
    const next: Record<string, string> = {}
    for (const [key, value] of Object.entries(view.filters)) {
      if (value != null && value !== "") next[key] = String(value)
    }
    router.get("/admin/niq-fragrances/dashboard", next, {
      preserveState: false,
    })
  }

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    if (!saveName.trim()) return
    setSaving(true)
    router.post(
      "/admin/niq-fragrances/dashboard/views",
      { niq_dashboard_view: { name: saveName.trim(), filters: activeQuery } },
      {
        preserveScroll: true,
        onFinish: () => {
          setSaving(false)
          setSaveOpen(false)
          setSaveName("")
        },
      },
    )
  }

  function handleRename(e: React.FormEvent) {
    e.preventDefault()
    if (!renameView || !renameName.trim()) return
    setRenaming(true)
    router.patch(
      `/admin/niq-fragrances/dashboard/views/${renameView.id}`,
      { niq_dashboard_view: { name: renameName.trim() } },
      {
        preserveScroll: true,
        onFinish: () => {
          setRenaming(false)
          setRenameView(null)
          setRenameName("")
        },
      },
    )
  }

  function handleDelete(view: SavedView) {
    if (!confirm(`Hapus tampilan "${view.name}"?`)) return
    router.delete(`/admin/niq-fragrances/dashboard/views/${view.id}`, {
      preserveScroll: true,
    })
  }

  return (
    <>
      <Head title="NIQ Fragrances — Dashboard">
        <meta
          name="description"
          content="Dashboard analisis pasar NIQ Fragrances: timeseries nilai penjualan bulanan yang dapat difilter per gender, brand, subcat, subsegb, channel rekonsiliasi, dan area."
        />
        <meta property="og:title" content="NIQ Fragrances — Dashboard Analisis Pasar" />
        <meta
          property="og:description"
          content="Visualisasi timeseries bulanan dari data NielsenIQ yang telah direkonsiliasi, dengan filter global yang tercermin di URL agar mudah dibagikan."
        />
      </Head>
      <AppShell>
        <div className="mb-2 flex items-center gap-1.5 text-xs text-ink-muted">
          <Link href="/admin/niq-fragrances" className="hover:text-ink-body">Data</Link>
          <span>/</span>
          <Link href="/admin/niq-fragrances" className="hover:text-ink-body">NIQ Fragrances</Link>
          <span>/</span>
          <span className="text-ink-body font-medium">Dashboard</span>
        </div>

        <div className="flex flex-col gap-4 border-b border-hairline pb-6 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-accent" />
              <h1>NIQ Fragrances — Dashboard</h1>
            </div>
            <p className="mt-2 max-w-2xl text-ink-muted">
              Analisis timeseries nilai penjualan bulanan dari data NielsenIQ yang
              telah direkonsiliasi. Atur filter di bawah — keadaannya tercermin di
              URL sehingga tampilan ini bisa dibagikan lewat tautan.
            </p>
          </div>
          <Button asChild variant="secondary" className="shrink-0">
            <a href={exportUrl} aria-disabled={!hasData}>
              <Download className="h-4 w-4" />
              Export Excel
            </a>
          </Button>
        </div>

        {/* Saved views */}
        <div className="mt-6" id="saved-views-section">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            <Bookmark className="h-4 w-4 shrink-0 text-ink-muted" />
            {saved_views.length === 0 && (
              <span className="text-sm text-ink-muted">Belum ada tampilan tersimpan</span>
            )}
            {saved_views.map((view) => (
              <div key={view.id} className="group flex shrink-0 items-center gap-0.5">
                <button
                  type="button"
                  onClick={() => applyView(view)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-sm transition-colors",
                    isActiveView(view)
                      ? "border-accent bg-accent/10 font-medium text-accent"
                      : "border-hairline bg-surface text-ink-body hover:border-accent/50 hover:text-accent",
                  )}
                >
                  {view.name}
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="rounded p-0.5 text-ink-muted opacity-0 transition-opacity hover:text-ink-body group-hover:opacity-100 focus:opacity-100"
                      aria-label={`Opsi untuk ${view.name}`}
                    >
                      <MoreVertical className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem
                      onSelect={() => {
                        setRenameView(view)
                        setRenameName(view.name)
                      }}
                    >
                      <Pencil />
                      Ubah nama
                    </DropdownMenuItem>
                    <DropdownMenuItem destructive onSelect={() => handleDelete(view)}>
                      <Trash2 />
                      Hapus
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                setSaveName("")
                setSaveOpen(true)
              }}
              className="flex shrink-0 items-center gap-1.5 rounded-full border border-dashed border-hairline px-3 py-1 text-sm text-ink-muted transition-colors hover:border-accent hover:text-accent"
            >
              <Plus className="h-3.5 w-3.5" />
              Simpan tampilan
            </button>
          </div>
        </div>

        {/* Save dialog */}
        <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
          <DialogContent size="sm">
            <form onSubmit={handleSave}>
              <DialogHeader>
                <DialogTitle>Simpan tampilan</DialogTitle>
                <DialogDescription>
                  Simpan kombinasi filter saat ini sebagai tampilan bernama agar bisa
                  diterapkan kembali dalam satu klik.
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4">
                <label htmlFor="saved-view-name">Nama tampilan</label>
                <Input
                  id="saved-view-name"
                  autoFocus
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  placeholder='Contoh: "Vitalis — channel rekonsiliasi"'
                  className="mt-1.5"
                  required
                />
              </div>
              <DialogFooter className="mt-6">
                <Button type="button" variant="secondary" onClick={() => setSaveOpen(false)}>
                  Batal
                </Button>
                <Button type="submit" disabled={saving || !saveName.trim()}>
                  {saving ? "Menyimpan…" : "Simpan"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Rename dialog */}
        <Dialog open={!!renameView} onOpenChange={(open) => !open && setRenameView(null)}>
          <DialogContent size="sm">
            <form onSubmit={handleRename}>
              <DialogHeader>
                <DialogTitle>Ubah nama tampilan</DialogTitle>
                <DialogDescription>
                  Ubah nama tampilan "{renameView?.name}".
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4">
                <label htmlFor="rename-view-name">Nama baru</label>
                <Input
                  id="rename-view-name"
                  autoFocus
                  value={renameName}
                  onChange={(e) => setRenameName(e.target.value)}
                  className="mt-1.5"
                  required
                />
              </div>
              <DialogFooter className="mt-6">
                <Button type="button" variant="secondary" onClick={() => setRenameView(null)}>
                  Batal
                </Button>
                <Button type="submit" disabled={renaming || !renameName.trim()}>
                  {renaming ? "Menyimpan…" : "Simpan"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Global filter bar */}
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Tipe dimensi">
            <Select
              value={filters.dimension_type}
              onChange={(e) => update({ dimension_type: e.target.value, dimension_value: null })}
            >
              {filter_options.dimension_types.map((dt) => (
                <option key={dt} value={dt}>{DIMENSION_TYPE_LABELS[dt] ?? dt}</option>
              ))}
            </Select>
          </Field>

          <Field label="Nilai dimensi">
            <Select
              value={filters.dimension_value ?? ""}
              onChange={(e) => update({ dimension_value: e.target.value || null })}
            >
              <option value="">Semua</option>
              {filter_options.dimension_values.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </Select>
          </Field>

          <Field label="Gender">
            <Select
              value={filters.gender ?? ""}
              onChange={(e) => update({ gender: e.target.value || null })}
            >
              <option value="">Semua</option>
              {filter_options.genders.map((g) => (
                <option key={g} value={g} className="capitalize">{g}</option>
              ))}
            </Select>
          </Field>

          <Field label="Brand">
            <Select
              value={filters.brand ?? ""}
              onChange={(e) => update({ brand: e.target.value || null })}
            >
              <option value="">Semua</option>
              {filter_options.brands.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </Select>
          </Field>

          <Field label="Subcat">
            <Select
              value={filters.subcat ?? ""}
              onChange={(e) => update({ subcat: e.target.value || null })}
            >
              <option value="">Semua</option>
              {filter_options.subcats.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          </Field>

          <Field label="Subsegb">
            <Select
              value={filters.subsegb ?? ""}
              onChange={(e) => update({ subsegb: e.target.value || null })}
            >
              <option value="">Semua</option>
              {filter_options.subsegbs.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          </Field>

          <Field label="Pecah berdasarkan">
            <Select
              value={breakdown}
              onChange={(e) => update({ breakdown: e.target.value })}
            >
              {breakdowns.map((b) => (
                <option key={b} value={b}>{BREAKDOWN_LABELS[b] ?? b}</option>
              ))}
            </Select>
          </Field>

          <Field label="Periode (SoW & Top-SKU)">
            <Select
              value={period ?? ""}
              onChange={(e) => update({ period: e.target.value || null })}
              disabled={periods.length === 0}
            >
              {periods.length === 0 && <option value="">Tidak ada data</option>}
              {periods.map((p) => (
                <option key={`${p.year}-${p.month}`} value={`${p.year}-${p.month}`}>
                  {p.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {/* Timeseries chart */}
        <div className="mt-8">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="text-lg font-semibold text-ink-display">
              Nilai penjualan bulanan
            </h2>
            {timeseries.months.length > 0 && (
              <span className="text-xs text-ink-muted">
                {timeseries.months[0].label} – {timeseries.months[timeseries.months.length - 1].label}
                {" · "}
                {timeseries.series.length} seri
              </span>
            )}
          </div>
          <div className="rounded-lg border border-hairline bg-surface p-4">
            <TimeseriesChart
              data={timeseries.rows}
              series={timeseries.series}
              valueFormatter={formatCompact}
              height={400}
              emptyMessage="Tidak ada data untuk kombinasi filter ini."
            />
          </div>
        </div>

        {/* Share of Wallet */}
        <div className="mt-10">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="text-lg font-semibold text-ink-display">Share of Wallet</h2>
            {selectedPeriodLabel && (
              <span className="text-xs text-ink-muted">Periode {selectedPeriodLabel}</span>
            )}
          </div>
          <p className="mb-4 max-w-2xl text-sm text-ink-muted">
            Komposisi nilai penjualan per channel (rekonsiliasi) dan per area untuk
            periode terpilih. Mengikuti filter gender/brand/subcat/subsegb; share
            menjumlah tepat 100% tanpa nilai negatif.
          </p>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <SowPanel
              title="Per channel"
              composition={share_of_wallet.channel}
            />
            <SowPanel
              title="Per area / region"
              composition={share_of_wallet.region}
            />
          </div>
        </div>

        {/* Top SKU performance */}
        <div className="mt-10">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="text-lg font-semibold text-ink-display">
              Top SKU (brand · variant · packsize)
            </h2>
            {selectedPeriodLabel && (
              <span className="text-xs text-ink-muted">
                Periode {selectedPeriodLabel} · pertumbuhan MoM
              </span>
            )}
          </div>
          <TopSkuTable rows={top_skus} />
        </div>
      </AppShell>
    </>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1.5 block">{label}</label>
      {children}
    </div>
  )
}

function SowPanel({ title, composition }: { title: string; composition: Composition }) {
  return (
    <div className="rounded-lg border border-hairline bg-surface p-4">
      <h3 className="mb-3 text-sm font-semibold text-ink-display">{title}</h3>
      <CompositionChart
        data={composition.slices}
        valueFormatter={formatCompact}
        height={220}
        emptyMessage="Tidak ada data untuk periode & filter ini."
      />
    </div>
  )
}

// Signed percentage, e.g. "+12,3%" / "-4,0%"; "Baru" when there is no prior
// month to compare against (growth_pct is null).
function formatGrowth(pct: number | null): { text: string; tone: string } {
  if (pct == null) return { text: "Baru", tone: "text-ink-muted" }
  const text = new Intl.NumberFormat("id-ID", {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    signDisplay: "exceptZero",
  }).format(pct)
  const tone =
    pct > 0
      ? "text-green-600 dark:text-green-400"
      : pct < 0
        ? "text-red-600 dark:text-red-400"
        : "text-ink-muted"
  return { text, tone }
}

function skuName(row: TopSku): string {
  return [row.variant, row.packsize].filter((v) => v && v !== "NA").join(" · ") || "—"
}

function TopSkuTable({ rows }: { rows: TopSku[] }) {
  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-lg border border-dashed border-hairline py-10 text-sm text-ink-muted">
        Tidak ada SKU untuk periode & filter ini.
      </div>
    )
  }

  return (
    <>
      {/* Desktop / tablet table */}
      <div className="hidden overflow-hidden rounded-lg border border-hairline sm:block">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-hairline bg-surface text-left text-xs text-ink-muted">
              <th scope="col" className="w-10 px-3 py-2 font-medium">#</th>
              <th scope="col" className="px-3 py-2 font-medium">Brand</th>
              <th scope="col" className="px-3 py-2 font-medium">Variant · Packsize</th>
              <th scope="col" className="px-3 py-2 text-right font-medium">Nilai</th>
              <th scope="col" className="px-3 py-2 text-right font-medium">Pertumbuhan MoM</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const g = formatGrowth(row.growth_pct)
              return (
                <tr key={`${row.brand}-${row.variant}-${row.packsize}-${i}`} className="border-b border-hairline last:border-0">
                  <td className="px-3 py-2 tabular-nums text-ink-muted">{i + 1}</td>
                  <th scope="row" className="px-3 py-2 text-left font-medium text-ink-display">{row.brand}</th>
                  <td className="px-3 py-2 text-ink-body">{skuName(row)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-ink-body">{formatCompact(row.value)}</td>
                  <td className={cn("px-3 py-2 text-right tabular-nums font-medium", g.tone)}>{g.text}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <ul className="space-y-2 sm:hidden">
        {rows.map((row, i) => {
          const g = formatGrowth(row.growth_pct)
          return (
            <li key={`${row.brand}-${row.variant}-${row.packsize}-${i}`} className="rounded-lg border border-hairline bg-surface p-3">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-medium text-ink-display">
                  <span className="mr-1.5 text-xs text-ink-muted">#{i + 1}</span>
                  {row.brand}
                </span>
                <span className={cn("tabular-nums text-sm font-medium", g.tone)}>{g.text}</span>
              </div>
              <div className="mt-1 flex items-baseline justify-between gap-2 text-sm">
                <span className="text-ink-muted">{skuName(row)}</span>
                <span className="tabular-nums text-ink-body">{formatCompact(row.value)}</span>
              </div>
            </li>
          )
        })}
      </ul>
    </>
  )
}
